import React, { useMemo } from 'react';
import '../styles/CreatePortfolioModal.css';
import { Input, Checkbox } from '../components';
import { numberFormatter, formatterLong } from '../styles/GlobalStyles';

function CreatePortfolioModal(props) {
    const leads = props.leads;
    const selectedLeadIds = props.selectedLeadIds;
    const onToggleLead = props.onToggleLead;
    const portfolioName = props.portfolioName;
    const setPortfolioName = props.setPortfolioName;
    const combinedAskingPrice = props.combinedAskingPrice;
    const setCombinedAskingPrice = props.setCombinedAskingPrice;
    const error = props.error;
    const searchTerm = props.searchTerm || '';
    const setSearchTerm = props.setSearchTerm;
    const searchResults = props.searchResults;
    const searchLoading = props.searchLoading || false;

    const formatCurrencyInput = (val) => {
        const cleaned = String(val).replace(/[^0-9]/g, '');
        if (cleaned === '') return '';
        return numberFormatter.format(parseInt(cleaned, 10));
    };

    const handlePriceChange = (e) => {
        const cleaned = e.target.value.replace(/[^0-9]/g, '');
        setCombinedAskingPrice(cleaned === '' ? '' : formatCurrencyInput(cleaned));
    };

    const formatAddress = (lead) => {
        if (!lead.address) return 'Unknown address';
        return lead.address.streetAddress || lead.address.label || 'Unknown address';
    };

    const formatCity = (lead) => {
        if (!lead.address) return '';
        const parts = [];
        if (lead.address.city) parts.push(lead.address.city);
        if (lead.address.state) parts.push(lead.address.state);
        return parts.join(', ');
    };

    const formatPrice = (price) => {
        if (price === null || price === undefined || isNaN(price)) return '-';
        return formatterLong.format(price).replace('.00', '');
    };

    // Build the display list as a single flat list (selected leads stay in place)
    const isSearching = searchTerm.length > 0;

    const { displayLeads, offPageSelectedLeads } = useMemo(() => {
        const selectedData = props.selectedLeadsData || {};
        const source = isSearching ? (searchResults || []) : leads;
        const sourceIdSet = new Set(source.map(l => l.leadId));

        // Find selected leads that aren't visible in the current source list
        const offPage = [];
        for (const leadId of selectedLeadIds) {
            if (!sourceIdSet.has(leadId)) {
                const fromPage = leads.find(l => l.leadId === leadId);
                const fromMap = selectedData[leadId];
                const leadObj = fromPage || fromMap;
                if (leadObj) offPage.push(leadObj);
            }
        }

        return { displayLeads: source, offPageSelectedLeads: offPage };
    }, [selectedLeadIds, leads, props.selectedLeadsData, isSearching, searchResults]);

    // Sum of individual asking prices for selected leads
    const individualTotal = useMemo(() => {
        const selectedData = props.selectedLeadsData || {};
        const source = isSearching ? (searchResults || []) : leads;
        let total = 0;
        for (const leadId of selectedLeadIds) {
            const lead = source.find(l => l.leadId === leadId) || selectedData[leadId];
            if (lead) {
                const price = lead.currentAskingPrice;
                if (price && !isNaN(price)) total += price;
            }
        }
        return total;
    }, [selectedLeadIds, leads, props.selectedLeadsData, isSearching, searchResults]);

    const renderLeadRow = (lead, isSelected) => {
        const inOtherPortfolio = lead.portfolioId && !selectedLeadIds.includes(lead.leadId);
        return (
            <div
                key={lead.leadId}
                className={`create-portfolio-lead-row ${isSelected ? 'create-portfolio-lead-row-selected' : ''} ${inOtherPortfolio ? 'create-portfolio-lead-row-disabled' : ''}`}
                onClick={() => !inOtherPortfolio && onToggleLead(lead.leadId, lead)}
            >
                <Checkbox
                    label={null}
                    selected={isSelected}
                    type="default"
                    disabled={!!inOtherPortfolio}
                    cta={() => {}}
                />
                <div className="create-portfolio-lead-info">
                    <span className="body-semibold colour-primary">
                        {formatAddress(lead)}
                    </span>
                    <span className="body-regular colour-secondary">
                        {formatCity(lead)}
                    </span>
                </div>
                <div className="create-portfolio-lead-price">
                    {inOtherPortfolio ? (
                        <span className="label-regular colour-secondary">In portfolio</span>
                    ) : (
                        <span className="body-regular colour-primary">
                            {formatPrice(lead.currentAskingPrice)}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="create-portfolio-modal-body">
            {/* Portfolio Name */}
            <div className="create-portfolio-section">
                <span className="body-bold colour-primary create-portfolio-section-label">
                    Portfolio Name
                </span>
                <Input
                    label={null}
                    labelType={null}
                    value={portfolioName}
                    onChange={(e) => setPortfolioName(e.target.value)}
                    placeholder="e.g. Downtown Bundle"
                    type="text"
                    leftUnit={null}
                    rightUnit={null}
                    unitSwitch={false}
                    leftUnitSelected={true}
                    unitSwitchFunc={null}
                    disabled={false}
                    multiline={false}
                    error={false}
                    onMouseEnter={null}
                    onMouseLeave={null}
                    reset={false}
                    resetFunc={null}
                    resetPosition={null}
                    focusMultiline={false}
                    prompt={false}
                    submitPromptDisabled={false}
                    submitPrompt={null}
                    onHeightChange={null}
                />
            </div>

            {/* Combined Asking Price */}
            <div className="create-portfolio-section">
                <span className="body-bold colour-primary create-portfolio-section-label">
                    Combined Asking Price
                </span>
                <Input
                    label={null}
                    labelType={null}
                    value={combinedAskingPrice}
                    onChange={handlePriceChange}
                    placeholder="0"
                    type="text"
                    leftUnit="$"
                    rightUnit={null}
                    unitSwitch={false}
                    leftUnitSelected={true}
                    unitSwitchFunc={null}
                    disabled={false}
                    multiline={false}
                    error={false}
                    onMouseEnter={null}
                    onMouseLeave={null}
                    reset={false}
                    resetFunc={null}
                    resetPosition={null}
                    focusMultiline={false}
                    prompt={false}
                    submitPromptDisabled={false}
                    submitPrompt={null}
                    onHeightChange={null}
                />
            </div>

            {/* Lead Selector */}
            <div className="create-portfolio-section">
                <span className="body-bold colour-primary create-portfolio-section-label">
                    Select Leads ({selectedLeadIds.length} selected)
                </span>
                <div className="create-portfolio-search">
                    <Input
                        label={null}
                        labelType={null}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by address..."
                        type="text"
                        leftUnit={null}
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={false}
                        multiline={false}
                        error={false}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={searchTerm.length > 0}
                        resetFunc={() => setSearchTerm('')}
                        resetPosition={null}
                        focusMultiline={false}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                </div>
                <div className="create-portfolio-leads-list">
                    {/* Off-page selected leads (selected from a different search/page) */}
                    {offPageSelectedLeads.length > 0 && (
                        <>
                            <div className="create-portfolio-offpage-label">
                                <span className="label-regular colour-secondary">
                                    Selected from other pages
                                </span>
                            </div>
                            {offPageSelectedLeads.map((lead) => renderLeadRow(lead, true))}
                            <div className="create-portfolio-lead-divider" />
                        </>
                    )}

                    {/* Main leads list (selected leads stay in place with highlight) */}
                    {searchLoading || (isSearching && searchResults === null) ? (
                        <div className="create-portfolio-leads-empty">
                            <span className="body-regular colour-secondary">Searching...</span>
                        </div>
                    ) : isSearching && searchResults && displayLeads.length === 0 ? (
                        <div className="create-portfolio-leads-empty">
                            <span className="body-regular colour-secondary">No leads found</span>
                        </div>
                    ) : (
                        displayLeads.map((lead) => renderLeadRow(lead, selectedLeadIds.includes(lead.leadId)))
                    )}
                </div>
            </div>

            {/* Summary */}
            {selectedLeadIds.length >= 2 && (
                <div className="create-portfolio-summary">
                    <div className="create-portfolio-summary-row">
                        <span className="body-regular colour-secondary">
                            Individual prices total
                        </span>
                        <span className="body-regular colour-primary">
                            {formatPrice(individualTotal)}
                        </span>
                    </div>
                    {combinedAskingPrice && (
                        <div className="create-portfolio-summary-row">
                            <span className="body-regular colour-secondary">
                                Combined portfolio price
                            </span>
                            <span className="body-bold colour-primary">
                                {formatPrice(parseInt(String(combinedAskingPrice).replace(/[^0-9]/g, ''), 10))}
                            </span>
                        </div>
                    )}
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="create-portfolio-error">
                    <span className="body-regular colour-error">{error}</span>
                </div>
            )}
        </div>
    );
}

export default CreatePortfolioModal;
